import { notifications } from "@mantine/notifications";
import {
    createFileRoute,
    type SearchSchemaInput,
    useNavigate,
    useSearch,
} from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import DatasourceAccessPendingModal from "src/components/DatasourceAccessPendingModal";
import DatasourceRequestAccessModal from "src/components/DatasourceRequestAccessModal";
import { useAvailableAlwaysOnDatasourceIds, useUser, useUserDatasources } from "src/hooks/useUser";
import { DocumentsRoute } from "../../containers/DocumentsRoute";
import { useDatasources } from "../../hooks/useDatasources";

type DocumentsSearch = {
    tab?: string;
};

export const Route = createFileRoute("/_layout-nosidebar/documents")({
    component: Documents,
    validateSearch: (search: DocumentsSearch & SearchSchemaInput): DocumentsSearch => ({
        tab: search.tab,
    }),
});

function Documents() {
    const navigate = useNavigate();
    const { tab } = useSearch({ from: "/_layout-nosidebar/documents" });

    const { data: chatVectorIndex = [], isSuccess } = useDatasources();
    const { data: userDatasourceDetails, isSuccess: isUserSuccess } = useUserDatasources();
    const availableAlwaysOnIds = useAvailableAlwaysOnDatasourceIds();
    const { data: user } = useUser();

    // Derived from the /v1/user payload so no separate access-requests call is
    // needed - the user endpoint already returns pending requests.
    const [pendingRequests, setPendingRequests] = useState<Set<string>>(new Set());
    useEffect(() => {
        const datasourceIds = new Set<string>();
        for (const req of user?.access_requests ?? []) {
            for (const ds of req.requested_datasources ?? []) {
                datasourceIds.add(ds.id);
            }
        }
        setPendingRequests(datasourceIds);
    }, [user]);

    const [deniedDatasource, setDeniedDatasource] = useState<{
        id: string;
        name: string;
        description: string;
        ingestion_status: string;
        pending: boolean;
    } | null>(null);

    const normalizeString = useCallback(
        (str: string | null | undefined) => (str ?? "").toLowerCase().replace(/[_\s]/g, ""),
        [],
    );

    // Role-granted datasource ids, sourced live from /v1/user (via useUserDatasources)
    // rather than the one-time-synced "userDatasources" localStorage snapshot, so
    // access changes (role updates, approved access requests, saved preferences)
    // are reflected without requiring a full page reload.
    const availableUserDatasourceIds = (userDatasourceDetails ?? [])
        .filter((ds) => ds.has_access)
        .map((ds) => ds.id);

    const getAvailableIndices = useCallback(() => {
        return chatVectorIndex.filter(
            (vectorIndex) =>
                availableAlwaysOnIds.has(vectorIndex.datasource_id) ||
                availableUserDatasourceIds.some(
                    (userDs) =>
                        normalizeString(userDs).includes(
                            normalizeString(vectorIndex.datasource_label),
                        ) ||
                        normalizeString(vectorIndex.datasource_label).includes(
                            normalizeString(userDs),
                        ) ||
                        normalizeString(userDs).includes(normalizeString(vectorIndex.label)) ||
                        normalizeString(vectorIndex.label).includes(normalizeString(userDs)) ||
                        normalizeString(userDs) === normalizeString(vectorIndex.datasource_id),
                ),
        );
    }, [chatVectorIndex, availableUserDatasourceIds, availableAlwaysOnIds, normalizeString]);

    type TabResolution =
        | { status: "valid"; index: string }
        | {
              status: "no-access";
              index: string;
              datasourceId: string;
              datasourceName: string;
              datasourceDescription: string;
              datasourceIngestionStatus: string;
          }
        | { status: "not-found"; index: string }
        | undefined;

    const resolveTab = useCallback(
        (tabValue: string | undefined): TabResolution => {
            if (!isSuccess || !isUserSuccess || chatVectorIndex.length === 0) return undefined;

            const available = getAvailableIndices();
            const allIndices = available.length > 0 ? available : chatVectorIndex;

            const resolveDefault = (): string | undefined => {
                const stored = localStorage.getItem("selectedIndex");
                if (stored) {
                    const storedMatch = allIndices.find((ds) => ds.id === stored);
                    if (storedMatch) return storedMatch.id;
                }
                return allIndices[0]?.id;
            };

            if (tabValue) {
                // Case 1: Exact match on composite ID (datasource_id/index_id)
                const exactMatch = allIndices.find((ds) => ds.id === tabValue);
                if (exactMatch) return { status: "valid", index: exactMatch.id };

                // Case 2: Datasource-only — pick first available index for that datasource
                const dsMatch = allIndices.find((ds) => ds.datasource_id === tabValue);
                if (dsMatch) return { status: "valid", index: dsMatch.id };

                // Case 3: Valid datasource, invalid index — parse datasource portion
                const tabDatasourceId = tabValue.includes("/") ? tabValue.split("/")[0] : tabValue;
                const dsPartialMatch = allIndices.find(
                    (ds) => ds.datasource_id === tabDatasourceId,
                );
                if (dsPartialMatch) return { status: "valid", index: dsPartialMatch.id };

                // Tab didn't match any accessible index — check if datasource exists
                // at all via the user endpoint (which returns ALL datasources
                // regardless of access, unlike the search endpoint).
                const allUserDatasources = userDatasourceDetails ?? [];
                const existsInUser = allUserDatasources.find(
                    (ds) => ds.id === tabValue || ds.id === tabDatasourceId,
                );

                const fallback = resolveDefault();
                if (!fallback) return undefined;

                if (existsInUser) {
                    // Datasource exists but user has no access
                    return {
                        status: "no-access",
                        index: fallback,
                        datasourceId: existsInUser.id,
                        datasourceName: existsInUser.name,
                        datasourceDescription: existsInUser.description,
                        datasourceIngestionStatus: existsInUser.ingestion_status,
                    };
                }

                // Datasource doesn't exist
                return { status: "not-found", index: fallback };
            }

            // No tab provided — use default
            const fallback = resolveDefault();
            return fallback ? { status: "valid", index: fallback } : undefined;
        },
        [isSuccess, isUserSuccess, chatVectorIndex, getAvailableIndices, userDatasourceDetails],
    );

    const [selectedIndex, setSelectedIndex] = useState<string>(() => {
        return localStorage.getItem("selectedIndex") ?? "";
    });

    // Track the previous tab value so we only react to real external tab changes
    // (browser back/forward, pasting a link), not unrelated effect re-runs.
    const prevTabRef = useRef<string | undefined>(undefined);

    // The initial selectedIndex can come straight from a possibly stale
    // localStorage value, so it must be validated against resolveTab at least
    // once before the "don't clobber the dropdown" guard below is allowed to skip it.
    const hasValidatedRef = useRef(false);

    useEffect(() => {
        const resolution = resolveTab(tab);

        // If data isn't ready yet, bail without updating prevTabRef so
        // we retry once the data arrives and the callback reference changes.
        if (!resolution) return;

        // On mount (prevTabRef is initialized to tab) or when tab actually changes
        const isTabChange = tab !== prevTabRef.current;
        prevTabRef.current = tab;

        // Skip if tab hasn't changed and we already have a validated selectedIndex —
        // this prevents the effect from overriding a synchronous dropdown update.
        if (!isTabChange && selectedIndex && hasValidatedRef.current) return;
        hasValidatedRef.current = true;

        const { index: resolved } = resolution;

        setSelectedIndex(resolved);
        localStorage.setItem("selectedIndex", resolved);

        if (resolution.status === "no-access") {
            setDeniedDatasource({
                id: resolution.datasourceId,
                name: resolution.datasourceName,
                description: resolution.datasourceDescription,
                ingestion_status: resolution.datasourceIngestionStatus,
                pending: pendingRequests.has(resolution.datasourceId),
            });
        } else {
            setDeniedDatasource(null);
        }

        if (resolution.status === "not-found") {
            notifications.show({
                title: "Data source not found",
                message: "The data source in the link could not be found.",
                color: "orange",
            });
        }

        // Don't replace the URL when showing the access modal — let the
        // modal's close/submit handle the redirect so the effect doesn't
        // re-fire and immediately clear the modal state.
        if (resolution.status !== "no-access" && tab !== resolved) {
            // biome-ignore lint/suspicious/noExplicitAny: TanStack Router generated types don't include search params
            navigate({ to: "/documents", search: { tab: resolved } as any, replace: true });
        }
    }, [tab, resolveTab, navigate, selectedIndex, pendingRequests]);

    // Called by the dropdown — sets state synchronously for immediate re-render,
    // then defers the URL update via setTimeout so the UI renders without waiting
    // for the router cycle.
    const handleSetSelectedIndex = useCallback(
        (value: string) => {
            setSelectedIndex(value);
            localStorage.setItem("selectedIndex", value);
            setTimeout(() => {
                // biome-ignore lint/suspicious/noExplicitAny: TanStack Router generated types don't include search params
                navigate({ to: "/documents", search: { tab: value } as any });
            });
        },
        [navigate],
    );

    const dismissDeniedDatasource = useCallback(() => {
        setDeniedDatasource(null);
        // biome-ignore lint/suspicious/noExplicitAny: TanStack Router generated types don't include search params
        navigate({ to: "/documents", search: { tab: selectedIndex } as any, replace: true });
    }, [navigate, selectedIndex]);

    if (!selectedIndex) return null;

    return (
        <>
            <DocumentsRoute index={selectedIndex} setSelectedIndex={handleSetSelectedIndex} />
            {deniedDatasource && !deniedDatasource.pending && (
                <DatasourceRequestAccessModal
                    opened
                    onClose={dismissDeniedDatasource}
                    onSubmit={() => {
                        setPendingRequests((prev) => new Set([...prev, deniedDatasource.id]));
                        notifications.show({
                            title: "Request submitted",
                            message: `Your access request for ${deniedDatasource.name} has been submitted. An admin will review it shortly.`,
                            color: "green",
                        });
                        dismissDeniedDatasource();
                    }}
                    title={`Request Access to ${deniedDatasource.name}`}
                    description={deniedDatasource.description}
                    ingestion_status={deniedDatasource.ingestion_status}
                    requested_datasources={[deniedDatasource.id]}
                />
            )}
            {deniedDatasource?.pending && (
                <DatasourceAccessPendingModal
                    opened
                    onClose={dismissDeniedDatasource}
                    datasourceName={deniedDatasource.name}
                />
            )}
        </>
    );
}
